/**
 * Custom modules in the module tree (brief §5.4, M3).
 *
 * `GET /api/modules` carries, next to the tenant config, the PUBLISHED custom
 * modules (`custom`, left out when there are none) and what the signed-in
 * person may do in each (`can`, the API's `CustomAccess`). Each becomes a
 * catalog module here (`customCatalogModule`) and joins the catalog BEFORE
 * resolution (`withCustomModules`), so it goes through the same
 * `resolveModuleTree` as every built-in module: the tenant's switch, name,
 * icon, ink and order (Admin ▸ Modules), the sidebar, the Hub tile, ⌘K, the
 * crumbs, `isRouteOff`, `tileVisibleFor` rule 0. The API does the same
 * (`CustomCatalog.With`).
 *
 * It lives at `/m/<slug>` (the same address space as team modules), its
 * rights namespace is `custom.<slug>` (`custom.<slug>:<view>` in User
 * Rights), and its pages are its views. A view the person may not open is
 * no rail row; a module they may not open at all has no tile.
 */
import type { Catalog, CatalogModule } from './catalog';

export const CUSTOM_TAG = 'custom';

export interface CustomCan {
  view: boolean;
  /** The views this person may open (forms only when they may add but not see). */
  views: string[];
  create: boolean;
  editOwn: boolean;
  editAll: boolean;
  delete: boolean;
  admin: boolean;
}

export interface CustomViewInfo {
  id: string;
  label: string;
  type: string;
  entity: string;
}

/** One entry of `GET /api/modules` → `custom`. */
export interface CustomModuleInfo {
  id: string;
  slug: string;
  version: number;
  label: string;
  description?: string | null;
  icon: string;
  ink: string;
  views: CustomViewInfo[];
  can: CustomCan;
}

export const NO_CAN: CustomCan = Object.freeze({
  view: false,
  views: [],
  create: false,
  editOwn: false,
  editAll: false,
  delete: false,
  admin: false,
}) as CustomCan;

export function customTile(slug: string): string {
  return `custom.${slug}`;
}

export function customRoute(slug: string): string {
  return `/m/${slug}`;
}

function obj(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

/** Read `custom` from the API leniently: a malformed entry is dropped, never thrown on. */
export function normalizeCustomModules(raw: unknown): CustomModuleInfo[] {
  if (!Array.isArray(raw)) return [];
  const out: CustomModuleInfo[] = [];
  const seen = new Set<string>();
  for (const x of raw) {
    const o = obj(x);
    if (!o || typeof o.slug !== 'string' || !/^[a-z][a-z0-9-]{1,30}$/.test(o.slug) || seen.has(o.slug)) continue;
    if (typeof o.label !== 'string' || typeof o.icon !== 'string' || typeof o.ink !== 'string') continue;
    if (!/^--[a-z][a-z0-9-]*$/.test(o.ink)) continue;
    const views: CustomViewInfo[] = Array.isArray(o.views)
      ? o.views
          .map(obj)
          .filter((v): v is Record<string, unknown> => !!v && typeof v.id === 'string' && typeof v.label === 'string')
          .map((v) => ({ id: v.id as string, label: v.label as string, type: String(v.type ?? ''), entity: String(v.entity ?? '') }))
      : [];
    if (!views.length) continue;
    const c = obj(o.can) ?? {};
    const can: CustomCan = {
      view: c.view === true,
      views: Array.isArray(c.views) ? c.views.filter((v): v is string => typeof v === 'string') : [],
      create: c.create === true,
      editOwn: c.editOwn === true,
      editAll: c.editAll === true,
      delete: c.delete === true,
      admin: c.admin === true,
    };
    seen.add(o.slug);
    out.push({
      id: String(o.id ?? o.slug),
      slug: o.slug,
      version: typeof o.version === 'number' ? o.version : 1,
      label: o.label,
      description: typeof o.description === 'string' ? o.description : null,
      icon: o.icon,
      ink: o.ink,
      views,
      can,
    });
  }
  return out;
}

/**
 * A custom module as a catalog module, for THIS person: every view is a
 * page (so the tenant's page switches and the rights registry line up with
 * the API's), the ones they may not open are no rail row, and the tile rule
 * is "always" for someone who may open a view, nobody otherwise.
 */
export function customCatalogModule(c: CustomModuleInfo): CatalogModule {
  const open = new Set(c.can.views);
  const blurb = c.description?.trim() || 'A module made for this workspace';
  return {
    id: c.slug,
    tile: customTile(c.slug),
    label: c.label,
    ink: c.ink,
    icon: c.icon,
    route: customRoute(c.slug),
    surface: 'shell',
    default: 'on',
    tags: [CUSTOM_TAG],
    access: open.size ? 'always' : 'role-only',
    roles: [],
    switcher: true,
    blurb: blurb.length > 90 ? `${blurb.slice(0, 87).trimEnd()}...` : blurb,
    hint: blurb.length > 90 ? `${blurb.slice(0, 87).trimEnd()}...` : blurb,
    groups: [
      {
        id: 'views',
        pages: c.views.map((v) => ({ slug: v.id, label: v.label, ...(open.has(v.id) ? {} : { navHidden: true as const }) })),
      },
    ],
  };
}

/**
 * The catalog plus these custom modules, after the built-in ones. None: the
 * very catalog object passed in, so Asian Trails resolves exactly as before.
 * A slug that collides with a built-in module is dropped (the API refuses it
 * on create; this is the belt).
 */
export function withCustomModules(catalog: Catalog, custom: readonly CustomModuleInfo[]): Catalog {
  const taken = new Set(catalog.modules.flatMap((m) => [m.id, m.tile]));
  const mods = custom.filter((c) => !taken.has(c.slug)).map(customCatalogModule);
  if (!mods.length) return catalog;
  return {
    ...catalog,
    modules: [...catalog.modules, ...mods],
    searchOrder: [...catalog.searchOrder, ...mods.map((m) => m.id)],
  };
}

export function isCustomModule(m: { tags?: string[] } | undefined | null): boolean {
  return !!m?.tags?.includes(CUSTOM_TAG);
}
